import {
  collectRoutines,
  dispatchAction,
  extractPayload,
  ofType,
  routine,
  spreadTap,
} from '@ardoq/rxbeach';
import {
  clearDashboardData,
  copyDashboardUrlWithStateIdButtonWasClicked,
  fetchDashboardData,
  fetchDashboardDataInBackground,
  persistLoadedDashboardData,
  setDashboardApiError,
  setDashboardLoadingState,
  setLoadedDashboard,
  setDashboardFolderId,
} from './DashboardBuilder/streams/actions';
import { LoadingState } from './DashboardBuilder/types';
import { distinctUntilChanged, filter, map, take, tap } from 'rxjs';
import {
  DashboardEventLocations,
  DashboardTrackingEventsNames,
  getTrackOpenedDashboardPayloadFor,
  MAINLINE,
  transformDashboardAPIData,
} from '@ardoq/dashboard';
import {
  dashboardDataRequest$,
  dashboardRouteNavigation$,
} from './navigation/dashboardRouteNavigation$';
import { DashboardModule } from 'dashboard/types';
import { switchMap, withLatestFrom } from 'rxjs/operators';
import { loadedDashboards$ } from './DashboardBuilder/loadedDashboards$';
import { dashboards$ } from 'streams/dashboards/dashboards$';
import { featureContext$ } from 'streams/featureContext$';
import { type FeatureContext } from '@ardoq/features';
import { isUsageMetricsEnabled } from './isUsageMetricsEnabled';
import { type DashboardDataRequest } from './navigation/types';
import { trackEvent } from 'tracking/tracking';
import { track, noTracking } from 'tracking/track';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import {
  fetchUsageMetricsDashboardData,
  persistMetricsDashboardData,
  setMetricsDashboardApiError,
} from './UsageMetrics/actions';
import { METRICS_DSB_ID } from './consts';
import {
  api,
  dashboardApi,
  getErrorStatusCode,
  handleError,
  presentationApi,
} from '@ardoq/api';
import {
  getArdoqErrorMessage,
  getArdoqErrorTraceId,
  isArdoqError,
} from '@ardoq/common-helpers';
import { isEmpty } from 'lodash';
import {
  copyDashboardArdoqURL,
  dashboardReaderUrl,
} from './DashboardReader/commands/copyUrlButtonCommands';
import { loadedDashboardsOperations } from './DashboardBuilder/loadedDashboardsOperations';
import {
  copyUrlWithFiltersDialogOpened,
  generateFiltersStateFailed,
  generateFiltersStateSucceeded,
} from '../report/copyUrlWithFiltersState/actions';

/**
 * This is meant to load data from branches other than the currently selected one,
 * so they are in sync (same filters applied) in order to compare their data.
 * It skips setting loading state and cache checks. Presentations are not supported yet.
 */
const handleFetchDashboardDataInBackground = routine(
  ofType(fetchDashboardDataInBackground),
  extractPayload(),
  switchMap(({ dashboardId, branchId, globalFilterValues }) =>
    dashboardApi.fetch({
      dashboardId,
      branchId,
      globalFilterValues,
    })
  ),
  handleError(error => {
    trackEvent(DashboardTrackingEventsNames.DASHBOARD_ERROR);
    api.logErrorIfNeeded(error);

    // Since it's a background load and it's currently only used in the
    // scenario analytics prototype, it doesn't set any error state.
    // TODO: decide how to handle this properly
  }),
  tap(response => {
    const dashboardData = transformDashboardAPIData(response);
    dispatchAction(persistLoadedDashboardData(dashboardData));
  })
);

// Builder and reader
export const handleFetchDashboardData = routine(
  ofType(fetchDashboardData),
  extractPayload(),
  switchMap(async ({ dashboardId, stateId, ...rest }) => {
    if (stateId) {
      const persistedViewState = await dashboardApi.fetchViewState(
        dashboardId,
        stateId
      );

      if (!isArdoqError(persistedViewState))
        return {
          ...rest,
          dashboardId,
          globalFilterValues: persistedViewState.state.dashboardFilterValues,
        };
    }
    return { dashboardId, ...rest };
  }),
  withLatestFrom(loadedDashboards$),
  map(
    ([
      {
        allowStaleWhileRevalidate,
        dashboardId,
        presentationId,
        slideId,
        branchId,
        globalFilterValues,
      },
      loadedDashboardsState,
    ]) => {
      const hasMatchingCache =
        loadedDashboardsOperations.hasCachedDashboardForFilters(
          loadedDashboardsState,
          dashboardId,
          branchId ?? MAINLINE,
          globalFilterValues
        );

      return {
        dashboardId,
        presentationId,
        slideId,
        branchId,
        globalFilterValues,
        serveStale: allowStaleWhileRevalidate && hasMatchingCache,
      };
    }
  ),
  tap(({ serveStale, dashboardId, globalFilterValues }) => {
    if (serveStale) {
      // The reader already renders the matching cached dashboard; clearing the
      // loading state stops a prior error or pending filter values from showing
      // on it while revalidation runs.
      dispatchAction(setDashboardLoadingState({ apiLoadingState: null }));
    } else {
      dispatchAction(
        setDashboardLoadingState({
          apiLoadingState: LoadingState.LOADING,
          apiLoadingDashboardId: dashboardId,
          apiLoadingGlobalFilterValues: globalFilterValues,
        })
      );
    }
  }),
  switchMap(
    ({
      dashboardId,
      presentationId,
      slideId,
      branchId,
      globalFilterValues,
    }) => {
      return presentationId && slideId
        ? presentationApi.fetchDashboardSlide({
            dashboardId,
            presentationId,
            slideId,
            globalFilterValues,
          })
        : dashboardApi.fetch({
            dashboardId,
            branchId,
            globalFilterValues,
          });
    }
  ),
  handleError(error => {
    // Trigger builder error state
    trackEvent(DashboardTrackingEventsNames.DASHBOARD_ERROR);
    api.logErrorIfNeeded(error);

    dispatchAction(
      setDashboardApiError({
        apiError: {
          message: getArdoqErrorMessage(error),
          traceId: getArdoqErrorTraceId(error),
          code: getErrorStatusCode(error),
        },
        apiLoadingState: LoadingState.LOADING_ERROR,
      })
    );
  }),
  tap(response => {
    // NOTE: If you make any changes here, make sure to also check if it's needed in usage metrics dashboard or discover!
    const dashboardData = transformDashboardAPIData(response);

    dispatchAction(persistLoadedDashboardData(dashboardData));

    // If a branch different than mainline is selected, fetch mainline as well
    // with matching filters for the side by side view
    if (dashboardData.scenarioBranchId) {
      dispatchAction(
        fetchDashboardDataInBackground({
          dashboardId: dashboardData._id,
          branchId: MAINLINE,
          ...(isEmpty(dashboardData.globalFilterValues)
            ? {}
            : { globalFilterValues: dashboardData.globalFilterValues }),
        })
      );
    }

    // Note: color settings are influenced by global filter values,
    // which influences the save button status in dashboard builder,
    // so only cache dashboard builder data when there is no global filter values for now
    if (isEmpty(dashboardData.globalFilterValues)) {
      dispatchAction(setLoadedDashboard(dashboardData));
    }
  })
);

export const loadDashboardDataForRoute = (
  {
    selectedDashboardId,
    presentationId,
    dashboardModule,
    globalFilterValues,
    stateId,
    folderId,
  }: DashboardDataRequest,
  featureContext: FeatureContext
): void => {
  if (dashboardModule === DashboardModule.USAGE_METRICS) {
    if (isUsageMetricsEnabled(featureContext)) {
      dispatchAction(fetchUsageMetricsDashboardData());
    }
    return;
  }
  if (!selectedDashboardId) {
    dispatchAction(clearDashboardData());
    if (folderId) {
      dispatchAction(setDashboardFolderId(folderId));
    }
    return;
  }
  dispatchAction(
    fetchDashboardData({
      dashboardId: selectedDashboardId,
      allowStaleWhileRevalidate:
        dashboardModule === DashboardModule.READER && !presentationId,
      presentationId,
      globalFilterValues,
      stateId,
    })
  );
};

const loadDashboardForRoute = dashboardDataRequest$.pipe(
  withLatestFrom(featureContext$),
  spreadTap(loadDashboardDataForRoute)
);

// Usage metrics dashboard (reader)
const handleFetchMetricsDashboardData = routine(
  ofType(fetchUsageMetricsDashboardData),
  switchMap(() => dashboardApi.fetch({ dashboardId: METRICS_DSB_ID })),
  handleError(error => {
    api.logErrorIfNeeded(error);

    dispatchAction(
      setMetricsDashboardApiError({
        message: getArdoqErrorMessage(error),
        traceId: getArdoqErrorTraceId(error),
        code: getErrorStatusCode(error),
      })
    );
  }),
  tap(response => {
    dispatchAction(persistMetricsDashboardData(response));
  })
);

const copyDashboardUrlWithStateIdRoutine = routine(
  ofType(copyDashboardUrlWithStateIdButtonWasClicked),
  extractPayload(),
  switchMap(({ id, dashboardFilterValues }) => {
    if (isEmpty(dashboardFilterValues)) {
      copyDashboardArdoqURL(id);
      return Promise.resolve(null);
    }
    dispatchAction(copyUrlWithFiltersDialogOpened());
    return dashboardApi.generateViewState(id, dashboardFilterValues);
  }),
  handleError(api.logErrorIfNeeded),
  tap(response => {
    if (isArdoqError(response)) {
      api.logErrorIfNeeded(response);
      dispatchAction(generateFiltersStateFailed());
    } else if (response && response.entityId && response.stateId) {
      dispatchAction(
        generateFiltersStateSucceeded(
          dashboardReaderUrl(response.entityId, response.stateId)
        )
      );
    }
  })
);

export const trackOpenedDashboardReaderRoutine = dashboardRouteNavigation$.pipe(
  distinctUntilChanged(
    (prev, curr) =>
      prev.selectedDashboardId === curr.selectedDashboardId &&
      prev.dashboardModule === curr.dashboardModule
  ),
  filter(
    ({ dashboardModule, selectedDashboardId }) =>
      dashboardModule === DashboardModule.READER && Boolean(selectedDashboardId)
  ),
  switchMap(({ selectedDashboardId }) =>
    dashboards$.pipe(
      map(({ dashboardsById }) => dashboardsById[selectedDashboardId!]),
      filter(Boolean),
      take(1)
    )
  ),
  tap(dashboard => {
    trackEvent(
      ...getTrackOpenedDashboardPayloadFor(
        dashboard,
        DashboardEventLocations.ARDOQ
      )
    );
  })
);

export const trackOpenedDashboardSlideRoutine = routine(
  ofType(fetchDashboardData),
  extractPayload(),
  filter(({ presentationId }) => Boolean(presentationId)),
  switchMap(({ dashboardId }) =>
    loadedDashboards$.pipe(
      map(state =>
        loadedDashboardsOperations.getLoadedDashboardData(state, dashboardId)
      ),
      filter(Boolean),
      take(1),
      withLatestFrom(currentUserPermissionContext$)
    )
  ),
  tap(([dashboard, permissionContext]) => {
    (permissionContext?.user?._id ? track : noTracking)(
      ...getTrackOpenedDashboardPayloadFor(
        dashboard,
        DashboardEventLocations.PRESENTATION
      )
    );
  })
);

export const routines = collectRoutines(
  loadDashboardForRoute,
  handleFetchDashboardData,
  handleFetchDashboardDataInBackground,
  handleFetchMetricsDashboardData,
  copyDashboardUrlWithStateIdRoutine,
  trackOpenedDashboardReaderRoutine,
  trackOpenedDashboardSlideRoutine
);
